import {
  SqlOperator,
  TimeTrackingReportsWhereColumn,
} from "@src/__generated__/graphql";
import { TIME_TRACKING_REPORT_SUBTASK_FILTER_COLUMN } from "@src/components/modules/time-tracking/Report/Store/Store";
import type { LinkProps } from "next/link";

export interface TimeReportFilter {
  column: TimeTrackingReportsWhereColumn;
  operator: SqlOperator;
  value: string[];
}

export interface TimeReportUrlOptions {
  projectId?: string;
  brandId?: string;
  budgetItemId?: string;
  taskId?: string;
  parentTaskId?: string;
  dateRange?: [string, string];
  additionalFilters?: TimeReportFilter[];
}

/**
 * Creates URL for time report with filters
 * Used in TaskItemComponents and project time report Store
 */
export function createTimeReportUrl(
  options: TimeReportUrlOptions,
): LinkProps["href"] {
  const {
    projectId,
    brandId,
    budgetItemId,
    taskId,
    parentTaskId,
    dateRange,
    additionalFilters,
  } = options;

  const AND: TimeReportFilter[] = [];

  if (projectId) {
    AND.push({
      column: TimeTrackingReportsWhereColumn.ProjectId,
      operator: SqlOperator.In,
      value: [projectId],
    });
  }

  if (dateRange && dateRange.length === 2) {
    AND.push({
      column: TimeTrackingReportsWhereColumn.TrackedForDate,
      operator: SqlOperator.Between,
      value: dateRange,
    });
  }

  if (brandId) {
    AND.push({
      column: TimeTrackingReportsWhereColumn.BrandId,
      operator: SqlOperator.In,
      value: [brandId],
    });
  }

  if (budgetItemId) {
    AND.push({
      column: TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
      operator: SqlOperator.In,
      value: [budgetItemId],
    });
  }

  if (parentTaskId) {
    AND.push({
      column: TimeTrackingReportsWhereColumn.TaskId,
      operator: SqlOperator.In,
      value: [parentTaskId],
    });
  }

  if (taskId) {
    AND.push({
      column: parentTaskId
        ? TIME_TRACKING_REPORT_SUBTASK_FILTER_COLUMN
        : TimeTrackingReportsWhereColumn.TaskId,
      operator: SqlOperator.In,
      value: [taskId],
    });
  }

  if (additionalFilters?.length) {
    AND.push(...additionalFilters);
  }

  const where = { AND };

  return {
    pathname: "/reports/time-report",
    query: { where: JSON.stringify(where) },
  };
}
